import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import type { BoardPagesAndParams } from '@aurora/shared-client/routes/endUserRoutes';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import type { MessageConstraints } from '@aurora/shared-generated/types/graphql-schema-types';
import { EndUserQueryParams } from '@aurora/shared-types/pages/enums';
import React, { useContext } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import { getBoardViewAllPageAndQueryPathParams } from '../../../../helpers/boards/ConversationStyleBehaviorHelper';
import type { IdeaStatusViewVariant, ItemType } from '../../../../types/enums';
import type { ItemViewVariantFC } from '../../../entities/types';
import localStyles from './IdeaStatusViewCard.module.pcss';
import MessageStatusLabel from '@aurora/shared-client/components/messages/MessageStatusLabel/MessageStatusLabel';
import type {
  IdeaStatusDetailsFragment,
  MessagesCountQuery,
  MessagesCountQueryVariables
} from '@aurora/shared-generated/types/graphql-types';
import messagesCountQuery from '@aurora/shared-client/components/messages/MessagesCount.query.graphql';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';

/**
 * Idea Status View - Card
 *
 * @author rishabh.jivan
 */
const IdeaStatusViewCard: ItemViewVariantFC<
  IdeaStatusDetailsFragment,
  ItemType.IDEA_STATUS,
  IdeaStatusViewVariant.CARD
> = ({ entity: ideaStatus, className, showIdeaCount }) => {
  const cx = useClassNameMapper(localStyles);
  const { contextNode } = useContext(AppContext);
  const { Link } = useEndUserRoutes();

  const constraints: MessageConstraints = {
    boardId: { eq: contextNode.id },
    statusKey: { eq: ideaStatus.statusKey },
    depth: { eq: 0 }
  };
  const { data: messageCountResult, loading: ideaCountLoading } = useQueryWithTracing<
    MessagesCountQuery,
    MessagesCountQueryVariables
  >(module, messagesCountQuery, {
    variables: {
      constraints
    },
    skip: !showIdeaCount
  });

  if (ideaCountLoading) {
    return null;
  }

  const ideasCount = messageCountResult && messageCountResult.messagesCount;
  const { page, pathParams } = getBoardViewAllPageAndQueryPathParams(contextNode);
  /**
   * Renders the idea status pill
   */
  function renderStatusPill(): React.ReactElement {
    return (
      <div className={cx('lia-featured-status-pill-wrapper')}>
        <MessageStatusLabel status={ideaStatus} className={cx('lia-featured-status-pill')} />
      </div>
    );
  }

  /**
   * Renders the number of idea posts which are in the given status
   */
  function renderIdeaCount(): React.ReactElement {
    return (
      <h4 className={cx('lia-featured-status-count')} data-testid="IdeaCount">
        {ideasCount ?? 0}
      </h4>
    );
  }

  /**
   * Renders the idea status card view
   */
  function renderCard(): React.ReactElement {
    return (
      <div data-testid="IdeaStatusViewCard" className={cx('lia-featured-status', className)}>
        {showIdeaCount && renderIdeaCount()}
        {renderStatusPill()}
      </div>
    );
  }

  return (
    <Link<BoardPagesAndParams>
      legacyBehavior={true}
      route={page}
      params={pathParams}
      query={{ [EndUserQueryParams.SEARCH_FILTER_BY_MESSAGE_STATUS]: ideaStatus.statusKey }}
      passHref
    >
      {renderCard()}
    </Link>
  );
};

export default IdeaStatusViewCard;
